import { addMinutes as addMinutesFns, format, parse } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export * from './weekdays'

const TIME_STRING_LENGTH = 5
const MINUTES_PER_HOUR = 60
export const MINUTES_PER_DAY = 1440
const ISO_DATE_PARTS = 3

/**
 * Parses a time string in 'HH:mm' format into a Date object.
 * Uses current date as the base.
 */
export function parseTime(time: string, baseDate: Date = new Date()): Date | undefined {
  if (!time || time.length !== TIME_STRING_LENGTH) return undefined
  try {
    const parsed = parse(time, 'HH:mm', baseDate)
    return Number.isNaN(parsed.getTime()) ? undefined : parsed
  } catch {
    return undefined
  }
}

/**
 * Formats a Date object into a 'HH:mm' string.
 */
export function formatTime(date?: Date): string {
  if (!date || (date instanceof Date && Number.isNaN(date.getTime()))) return ''
  return format(date, 'HH:mm')
}

/**
 * Formats a Date object or timestamp with ptBR locale.
 */
export function formatDate(date: Date | number | string, formatStr: string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (d instanceof Date && Number.isNaN(d.getTime())) return ''
  return format(d, formatStr, { locale: ptBR })
}

/**
 * Formats a Date object into an ISO date string (yyyy-MM-dd).
 */
export function toIsoDate(date?: Date): string {
  if (!date) {
    return ''
  }

  return formatDate(date, 'yyyy-MM-dd')
}

/**
 * Parses an ISO date string (yyyy-MM-dd) into a Date object.
 */
export function parseIsoDate(date?: string): Date | undefined {
  if (!date) {
    return undefined
  }

  const parts = date.split('-').map(Number)
  if (
    parts.length !== ISO_DATE_PARTS ||
    parts.some((part) => Number.isNaN(part)) ||
    parts[0] === undefined ||
    parts[1] === undefined ||
    parts[2] === undefined
  ) {
    return undefined
  }

  return new Date(parts[0], parts[1] - 1, parts[2])
}

/**
 * Alias to keep call sites explicit when converting Date to yyyy-MM-dd.
 */
export function formatIsoDate(date?: Date): string {
  return toIsoDate(date)
}

/**
 * Wraps date-fns addMinutes for convenience.
 */
export function addMinutes(date: Date, minutes: number): Date {
  return addMinutesFns(date, minutes)
}

const TIME_PATTERN = /^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/

function parseMinutes(time: string): number | null {
  const match = TIME_PATTERN.exec(time)
  if (!match) return null
  return Number(match[1]) * MINUTES_PER_HOUR + Number(match[2])
}

/**
 * Gets total minutes from a 'HH:mm' or 'HH:mm:ss' time string (seconds are ignored).
 * Returns 0 for anything else.
 */
export function getMinutesFromTime(time: string): number {
  return parseMinutes(time) ?? 0
}

/**
 * Converts a start/end pair of time strings into minutes since midnight of the start day.
 * An end at or before the start means midnight-crossing: the end belongs to the following day,
 * so MINUTES_PER_DAY is added to it (same rule as the rental API). This includes end === start,
 * which is a full 24 hours.
 * Example: ('23:00', '00:00') -> { start: 1380, end: 1440 }
 */
export function getTimeRangeMinutes(
  startTime: string,
  endTime: string,
): { start: number; end: number } {
  const start = getMinutesFromTime(startTime)
  const end = getMinutesFromTime(endTime)
  return { start, end: end <= start ? end + MINUTES_PER_DAY : end }
}

/**
 * Duration in minutes between two time strings, wrapping past midnight
 * (see getTimeRangeMinutes).
 */
export function getDurationMinutes(startTime: string, endTime: string): number {
  const { start, end } = getTimeRangeMinutes(startTime, endTime)
  return end - start
}

/**
 * Calculates and formats the duration between two time strings in HH:mm (or HH:mm:ss) format.
 * An end at or before the start is treated as next-day midnight (see getTimeRangeMinutes).
 * Returns null if either input is empty or unparsable.
 * Example: "1h 30m", "0h 45m", "2h 00m"
 */
export function calculateDuration(startTime: string, endTime: string): string | null {
  if (parseMinutes(startTime) === null || parseMinutes(endTime) === null) return null

  const diffInMinutes = getDurationMinutes(startTime, endTime)

  const hours = Math.floor(diffInMinutes / MINUTES_PER_HOUR)
  const minutes = diffInMinutes % MINUTES_PER_HOUR

  const hoursPart = hours > 0 ? `${hours}h` : ''
  const minutesPart =
    minutes > 0 ? `${minutes.toString().padStart(2, '0')}m` : hours === 0 ? '0m' : '00m'

  return `${hoursPart} ${minutesPart}`.trim()
}

/**
 * Formats a duration in minutes to pt-BR human-readable text.
 * Examples: 60 -> "1 hora", 120 -> "2 horas", 90 -> "90 min"
 */
export function formatDurationInHours(durationInMinutes: number): string {
  if (!Number.isFinite(durationInMinutes) || durationInMinutes <= 0) {
    return '0 min'
  }

  if (durationInMinutes === MINUTES_PER_HOUR) {
    return '1 hora'
  }

  if (durationInMinutes % MINUTES_PER_HOUR === 0) {
    return `${durationInMinutes / MINUTES_PER_HOUR} horas`
  }

  return `${durationInMinutes} min`
}
